import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { defaultExperience, experienceSchema } from "@reactive-resume/schema";
import {
  DateRangePicker,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  RichInput,
} from "@reactive-resume/ui";
import { useForm } from "react-hook-form";
import type { z } from "zod";

import { AiActions } from "@/client/components/ai-actions";

import { SectionDialog } from "../sections/shared/section-dialog";
import { URLInput } from "../sections/shared/url-input";

const formSchema = experienceSchema;

type FormValues = z.infer<typeof formSchema>;

export const ExperienceDialog = () => {
  const form = useForm<FormValues>({
    defaultValues: defaultExperience,
    resolver: zodResolver(formSchema),
  });

  return (
    <SectionDialog<FormValues> id="experience" form={form} defaultValues={defaultExperience}>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField
          name="company"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t`Company`}</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="position"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t({
                  message: "Position",
                  context: "Position held at a company, for example, Software Engineer",
                })}
              </FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="date"
          control={form.control}
          render={({ field }) => {
            const { i18n } = useLingui();
            const isChinese = i18n.locale.startsWith("zh");
            
            if (isChinese) {
              // 解析日期范围值
              const parseDateRange = (dateStr: string) => {
                console.log('Experience parseDateRange - Parsing date string:', dateStr);
                
                if (!dateStr || dateStr.trim() === '') {
                  console.log('Experience parseDateRange - Empty date string, returning empty start/end');
                  return { start: '', end: '' };
                }

                const trimmed = dateStr.trim();
                
                // Check if it's a date range (contains '至')
                if (trimmed.includes('至')) {
                  const [start, end] = trimmed.split('至');
                  console.log('Experience parseDateRange - Parsed as date range:', { start: start?.trim() || '', end: end?.trim() || '' });
                  return { start: start?.trim() || '', end: end?.trim() || '' };
                } else {
                  // Single date - return the complete date as start date with empty end
                  console.log('Experience parseDateRange - Parsed as single date:', { start: trimmed, end: '' });
                  return { start: trimmed, end: '' };
                }
              };
              
              const { start, end } = parseDateRange(field.value || "");
              console.log('Experience - Current field value:', field.value, 'Parsed start:', start, 'Parsed end:', end);
              
              return (
                <FormItem>
                  <FormLabel>日期或日期范围</FormLabel>
                  <FormControl>
                    <DateRangePicker
                      startValue={start}
                      endValue={end}
                      onChange={(startDate, endDate) => {
                        if (startDate && endDate) {
                          field.onChange(`${startDate}至${endDate}`);
                        } else if (startDate) {
                          field.onChange(startDate);
                        } else {
                          field.onChange("");
                        }
                      }}
                      placeholder="选择日期范围"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }
            
            return (
              <FormItem>
                <FormLabel>{t`Date or Date Range`}</FormLabel>
                <FormControl>
                  <Input {...field} placeholder={t`March 2023 - Present`} />
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />

        <FormField
          name="location"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t`Location`}</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="url"
          control={form.control}
          render={({ field }) => (
            <FormItem className="sm:col-span-2">
              <FormLabel>{t`Website`}</FormLabel>
              <FormControl>
                <URLInput {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="summary"
          control={form.control}
          render={({ field }) => (
            <FormItem className="sm:col-span-2">
              <FormLabel>{t`Summary`}</FormLabel>
              <FormControl>
                <RichInput
                  {...field}
                  content={field.value}
                  footer={(editor) => (
                    <AiActions
                      value={editor.getText()}
                      onChange={(value) => {
                        editor.commands.setContent(value, true);
                        field.onChange(value);
                      }}
                    />
                  )}
                  onChange={(value) => {
                    field.onChange(value);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </SectionDialog>
  );
};
